import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import {
  Send, Users, UserCheck, Search, X, MessageSquare, Eye, Check,
  Filter, History, ChevronRight, Loader2,
} from "lucide-react";
import { formatDateTime } from "@/lib/dates";

const SMS_SEGMENT = 160;
const MAX_SEGMENTS = 3;
const MAX_CHARS = SMS_SEGMENT * MAX_SEGMENTS;

const segmentsFor = (s) => {
  const n = (s || "").length;
  if (n === 0) return 0;
  if (n <= SMS_SEGMENT) return 1;
  return 1 + Math.ceil((n - SMS_SEGMENT) / 153);
};

export default function BulkSmsPage() {
  const [tab, setTab] = useState("compose"); // 'compose' | 'history'
  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">HR Comms</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><MessageSquare className="w-8 h-8" /> Bulk SMS</h1>
          <p className="text-[var(--cc-text-soft)] mt-1 max-w-2xl">Send one text to many employees or candidates. Personalise with merge fields like <code>{"{{first_name}}"}</code>. Each recipient is logged individually in the SMS Log.</p>
        </div>
        <div className="flex gap-1 cc-card p-1">
          <button className={`btn-ghost text-xs ${tab === "compose" ? "bg-[var(--cc-surface)]" : ""}`} onClick={() => setTab("compose")} data-testid="tab-compose">Compose</button>
          <button className={`btn-ghost text-xs ${tab === "history" ? "bg-[var(--cc-surface)]" : ""}`} onClick={() => setTab("history")} data-testid="tab-history"><History className="w-3 h-3 inline mr-1" /> History</button>
        </div>
      </div>

      {tab === "compose" ? <ComposeView /> : <HistoryView />}
    </div>
  );
}

// ============================================================================
//  COMPOSE
// ============================================================================
function ComposeView() {
  const [recipients, setRecipients] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [filterKind, setFilterKind] = useState("all"); // all | employee | candidate
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set()); // key = "kind:id"
  const [message, setMessage] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("kind", filterKind);
      if (q.trim()) params.set("q", q.trim());
      if (stage) params.set("stage", stage);
      const { data } = await api.get(`/admin/bulk-sms/recipients?${params}`);
      setRecipients(data.recipients || []);
      setMergeFields(data.merge_fields || []);
    } catch {
      toast.error("Failed to load recipients");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchRecipients(); /* eslint-disable-next-line */ }, [filterKind, stage]);

  const onSearchKey = (e) => { if (e.key === "Enter") fetchRecipients(); };

  const visible = recipients;
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(`${r.kind}:${r.id}`));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) visible.forEach((r) => next.delete(`${r.kind}:${r.id}`));
    else visible.forEach((r) => next.add(`${r.kind}:${r.id}`));
    setSelected(next);
  };
  const toggleOne = (r) => {
    const k = `${r.kind}:${r.id}`;
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const segs = segmentsFor(message);
  const remaining = MAX_CHARS - message.length;
  const tooLong = message.length > MAX_CHARS;

  const insertField = (key) => {
    setMessage((m) => `${m}{{${key}}}`);
  };

  const recipientIds = useMemo(() => Array.from(selected), [selected]);

  const doPreview = async () => {
    if (recipientIds.length === 0) return toast.error("Pick at least one recipient");
    if (!message.trim()) return toast.error("Type a message first");
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/admin/bulk-sms/preview", { message, recipient_ids: recipientIds });
      setPreviewData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewBusy(false); }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/bulk-sms/send", {
        message,
        recipient_ids: recipientIds,
        campaign_name: campaignName || undefined,
      });
      setResult(data);
      setConfirmOpen(false);
      toast.success(`Sent ${data.sent}/${data.total}${data.failed ? ` · ${data.failed} failed` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const reset = () => { setResult(null); setMessage(""); setCampaignName(""); setSelected(new Set()); setPreviewData(null); };

  if (result) return <SendResultCard result={result} onReset={reset} />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      {/* LEFT — recipient picker */}
      <div className="cc-card p-4 lg:col-span-3">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><Users className="w-5 h-5" /> Recipients</h2>
          <div className="text-xs"><strong>{selected.size}</strong> selected</div>
        </div>
        <div className="grid grid-cols-12 gap-2 mb-3">
          <div className="col-span-7 relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[var(--cc-text-soft)]" />
            <input className="cc-input pl-7" placeholder="Search by name, email, phone, job…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey} data-testid="recipient-search" />
          </div>
          <select className="cc-select col-span-3" value={filterKind} onChange={(e) => setFilterKind(e.target.value)} data-testid="recipient-kind-filter">
            <option value="all">All</option>
            <option value="employee">Employees</option>
            <option value="candidate">Candidates</option>
          </select>
          <button className="btn-secondary col-span-2 text-xs" onClick={fetchRecipients} data-testid="recipient-refresh">{loading ? "…" : "Search"}</button>
        </div>

        <div className="border border-[var(--cc-border)] rounded-xl overflow-hidden max-h-[58vh] overflow-y-auto" data-testid="recipient-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--cc-surface)] sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="recipient-select-all" /></th>
                <th className="px-3 py-2 label-eyebrow">Name</th>
                <th className="px-3 py-2 label-eyebrow">Type</th>
                <th className="px-3 py-2 label-eyebrow">Phone</th>
                <th className="px-3 py-2 label-eyebrow">Role / Stage</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && !loading && (
                <tr><td colSpan="5" className="text-center py-8 text-[var(--cc-text-soft)]" data-testid="recipient-empty">No recipients with a phone number on file.</td></tr>
              )}
              {visible.map((r) => {
                const k = `${r.kind}:${r.id}`;
                const checked = selected.has(k);
                return (
                  <tr key={k} className={`border-t border-[var(--cc-border)] cursor-pointer hover:bg-[var(--cc-surface)] ${checked ? "bg-[var(--cc-surface)]" : ""}`}
                      onClick={() => toggleOne(r)} data-testid={`recipient-row-${r.kind}-${r.id}`}>
                    <td className="px-3 py-1.5"><input type="checkbox" checked={checked} onChange={() => toggleOne(r)} onClick={(e) => e.stopPropagation()} /></td>
                    <td className="px-3 py-1.5 font-medium">{r.name}</td>
                    <td className="px-3 py-1.5">
                      <span className={`pill ${r.kind === "employee" ? "pill-success" : "pill-info"} inline-flex items-center gap-1 text-[10px]`}>
                        {r.kind === "employee" ? <UserCheck className="w-3 h-3" /> : <Users className="w-3 h-3" />} {r.kind}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">{r.phone}</td>
                    <td className="px-3 py-1.5 text-xs text-[var(--cc-text-soft)]">{r.job_title || "—"}{r.stage ? ` · ${r.stage}` : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* RIGHT — composer */}
      <div className="cc-card p-4 lg:col-span-2 flex flex-col">
        <h2 className="font-display text-lg font-bold mb-3 inline-flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Message</h2>

        <input className="cc-input mb-2 text-sm" placeholder="Campaign name (optional, for History)" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} data-testid="campaign-name-input" />

        <textarea
          className={`cc-input text-sm font-mono ${tooLong ? "border-[var(--cc-accent)]" : ""}`}
          rows={8}
          placeholder="Hi {{first_name}}, this is a reminder from Rabah Concept — please check your portal for your training due-date."
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
          data-testid="message-textarea"
        />
        <div className="flex justify-between text-[11px] mt-1 mb-3">
          <span className={tooLong ? "text-[var(--cc-accent)] font-semibold" : "text-[var(--cc-text-soft)]"} data-testid="sms-counter">
            {message.length} chars · {segs} segment{segs === 1 ? "" : "s"}{tooLong ? ` · exceeds ${MAX_SEGMENTS}-segment cap` : ""}
          </span>
          <span className="text-[var(--cc-text-soft)]">Voodoo SMS</span>
        </div>

        <div className="mb-3">
          <div className="label-eyebrow mb-1.5">Merge fields</div>
          <div className="flex flex-wrap gap-1">
            {mergeFields.map((f) => (
              <button key={f.key} className="text-[11px] px-2 py-1 rounded-md border border-[var(--cc-border)] hover:bg-[var(--cc-surface)] font-mono"
                      onClick={() => insertField(f.key)} title={`Inserts ${f.label} — e.g. "${f.example}"`}
                      data-testid={`merge-${f.key}`}>
                {`{{${f.key}}}`}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-between gap-2 mt-auto">
          <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={doPreview} disabled={previewBusy || selected.size === 0 || !message.trim()} data-testid="preview-button">
            <Eye className="w-3.5 h-3.5" /> {previewBusy ? "Loading…" : "Preview"}
          </button>
          <button className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={() => setConfirmOpen(true)} disabled={selected.size === 0 || !message.trim() || tooLong} data-testid="send-button">
            <Send className="w-3.5 h-3.5" /> Send to {selected.size}
          </button>
        </div>

        {previewData && (
          <div className="mt-4 border-t border-[var(--cc-border)] pt-3" data-testid="preview-panel">
            <div className="label-eyebrow mb-1.5">Preview (first {previewData.samples.length} of {previewData.total_recipients})</div>
            <div className="space-y-1.5">
              {previewData.samples.map((s) => (
                <div key={s.recipient_id} className="text-xs cc-card p-2" data-testid={`preview-sample-${s.recipient_id}`}>
                  <div className="font-medium">{s.name} <span className="text-[var(--cc-text-soft)] font-mono ml-1">{s.phone}</span></div>
                  <div className="font-mono text-[11px] mt-1 whitespace-pre-wrap">{s.rendered}</div>
                  <div className="text-[10px] text-[var(--cc-text-soft)] mt-0.5">{s.length} chars · {s.segments} segment{s.segments === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {confirmOpen && (
        <Modal onClose={() => !sending && setConfirmOpen(false)} testId="confirm-send-modal" widthClass="max-w-md">
          <h3 className="font-display text-xl font-bold mb-2">Send this SMS?</h3>
          <p className="text-sm text-[var(--cc-text-soft)] mb-3">
            You are about to send to <strong>{selected.size}</strong> recipient{selected.size === 1 ? "" : "s"} ({segs} segment{segs === 1 ? "" : "s"} each). This will use SMS credits and cannot be undone.
          </p>
          <div className="cc-card p-2 text-xs font-mono whitespace-pre-wrap break-words bg-[var(--cc-surface)]">{message}</div>
          <div className="flex justify-end gap-2 mt-4">
            <button className="btn-ghost" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</button>
            <button className="btn-primary inline-flex items-center gap-1.5" onClick={doSend} disabled={sending} data-testid="confirm-send-button">
              {sending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5" /> Confirm send</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SendResultCard({ result, onReset }) {
  const okPct = result.total ? Math.round((result.sent / result.total) * 100) : 0;
  return (
    <div className="cc-card p-6 max-w-2xl" data-testid="send-result-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-[var(--cc-sage-light)] flex items-center justify-center"><Check className="w-6 h-6 text-[var(--cc-sage)]" /></div>
        <div>
          <h2 className="font-display text-2xl font-bold">Sent</h2>
          <div className="text-sm text-[var(--cc-text-soft)]">{result.name}</div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Stat label="Total" value={result.total} />
        <Stat label="Sent"  value={result.sent}  tone="success" />
        <Stat label="Failed" value={result.failed} tone={result.failed ? "danger" : undefined} />
        <Stat label="Invalid" value={result.invalid} tone={result.invalid ? "warning" : undefined} />
      </div>
      <div className="text-xs text-[var(--cc-text-soft)] mb-4">{okPct}% delivered · campaign id <code>{result.id.slice(0,8)}</code></div>
      {result.failed > 0 && (
        <details className="cc-card p-3 mb-3">
          <summary className="text-xs font-semibold cursor-pointer">Failures ({result.failed})</summary>
          <ul className="mt-2 text-xs space-y-1">
            {(result.recipients || []).filter((r) => r.status !== "sent" && r.status !== "mocked").map((r) => (
              <li key={r.recipient_id}><strong>{r.name || r.recipient_id}</strong> — {r.error || r.status}</li>
            ))}
          </ul>
        </details>
      )}
      <button className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={onReset} data-testid="send-another-button">
        <ChevronRight className="w-3.5 h-3.5" /> Send another
      </button>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "success" ? "text-[var(--cc-sage)]" : tone === "danger" ? "text-[var(--cc-accent)]" : tone === "warning" ? "text-amber-600" : "";
  return (
    <div className="cc-card p-2.5 text-center">
      <div className={`font-display text-2xl font-bold ${cls}`}>{value}</div>
      <div className="label-eyebrow text-[10px]">{label}</div>
    </div>
  );
}

// ============================================================================
//  HISTORY
// ============================================================================
function HistoryView() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bulk-sms/campaigns?limit=100");
      setRows(data || []);
    } catch { toast.error("Failed to load history"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCampaign = async (id) => {
    try {
      const { data } = await api.get(`/admin/bulk-sms/campaigns/${id}`);
      setActive(data);
    } catch { toast.error("Failed to load campaign"); }
  };

  return (
    <div className="cc-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[var(--cc-surface)]">
          <tr className="text-left">
            <th className="px-4 py-2 label-eyebrow">When</th>
            <th className="px-4 py-2 label-eyebrow">Campaign</th>
            <th className="px-4 py-2 label-eyebrow">By</th>
            <th className="px-4 py-2 label-eyebrow">Total</th>
            <th className="px-4 py-2 label-eyebrow">Sent</th>
            <th className="px-4 py-2 label-eyebrow">Failed</th>
            <th className="px-4 py-2 label-eyebrow"></th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan="7" className="text-center py-8 text-[var(--cc-text-soft)]">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-[var(--cc-text-soft)]" data-testid="history-empty">No campaigns yet.</td></tr>}
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-[var(--cc-border)]" data-testid={`campaign-row-${c.id}`}>
              <td className="px-4 py-2 text-xs">{formatDateTime(c.created_at)}</td>
              <td className="px-4 py-2 font-medium">{c.name}</td>
              <td className="px-4 py-2 text-xs">{c.created_by_name}</td>
              <td className="px-4 py-2 text-xs">{c.total}</td>
              <td className="px-4 py-2 text-xs text-[var(--cc-sage)] font-semibold">{c.sent}</td>
              <td className="px-4 py-2 text-xs">{c.failed > 0 ? <span className="text-[var(--cc-accent)] font-semibold">{c.failed}</span> : "0"}</td>
              <td className="px-4 py-2 text-right">
                <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => openCampaign(c.id)} data-testid={`campaign-view-${c.id}`}><Eye className="w-3 h-3" /> View</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {active && (
        <Modal onClose={() => setActive(null)} widthClass="max-w-3xl" testId="campaign-detail-modal">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold">{active.name}</h2>
            <button className="btn-ghost" onClick={() => setActive(null)}><X className="w-4 h-4" /></button>
          </div>
          <div className="text-xs text-[var(--cc-text-soft)] mb-3">Sent {formatDateTime(active.created_at)} by {active.created_by_name}</div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <Stat label="Total" value={active.total} />
            <Stat label="Sent" value={active.sent} tone="success" />
            <Stat label="Failed" value={active.failed} tone={active.failed ? "danger" : undefined} />
            <Stat label="Invalid" value={active.invalid} tone={active.invalid ? "warning" : undefined} />
          </div>
          <div className="label-eyebrow mb-1">Message template</div>
          <pre className="cc-card p-2.5 text-xs font-mono whitespace-pre-wrap mb-3">{active.message_template}</pre>
          <div className="label-eyebrow mb-1">Per-recipient log ({active.recipients?.length || 0})</div>
          <div className="max-h-72 overflow-y-auto border border-[var(--cc-border)] rounded-lg">
            <table className="w-full text-xs">
              <tbody>
                {(active.recipients || []).map((r) => (
                  <tr key={r.recipient_id} className="border-t border-[var(--cc-border)]">
                    <td className="px-3 py-1.5">{r.name || r.recipient_id}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px]">{r.phone || "—"}</td>
                    <td className="px-3 py-1.5">
                      <span className={`pill ${r.status === "sent" || r.status === "mocked" ? "pill-success" : r.status === "invalid" ? "pill-warning" : "pill-danger"} text-[10px]`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-[var(--cc-text-soft)]">{r.error || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
}
